import {
  Wrench,
  Plus,
  Search,
  Edit2,
  Trash2,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  Save,
  Filter,
  ChevronDown,
  Mail,
  Phone,
  Star,
  StarOff,
  User,
  FileText,
  Users,
  Building2,
  UserCheck,
  UserX,
} from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';

import { supabase } from '../../lib/supabase';
import { useAuth } from '../../providers/AuthProvider';

// ── Types ───────────────────────────────────────────────────────────────────

interface Technician {
  id: string;
  organization_id: string;
  worker_id: string | null;
  supplier_id: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  specializations: string[];
  availability_status: 'available' | 'busy' | 'off_duty' | 'on_leave';
  is_internal: boolean;
  rating: number | null;
  total_jobs_completed: number;
  average_response_hours: number | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface TechnicianFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  specializations: string[];
  availability_status: string;
  is_internal: boolean;
  rating: number;
  notes: string;
}

// ── Constants ───────────────────────────────────────────────────────────────

const SPECIALIZATIONS = [
  { value: 'refrigeration', label: 'Chłodnictwo' },
  { value: 'hvac', label: 'Klimatyzacja' },
  { value: 'electrical', label: 'Elektryka' },
  { value: 'plumbing', label: 'Hydraulika' },
  { value: 'mechanical', label: 'Mechanika' },
  { value: 'electronics', label: 'Elektronika' },
  { value: 'iot_sensors', label: 'Czujniki IoT' },
  { value: 'oven_maintenance', label: 'Serwis pieców' },
  { value: 'mixer_maintenance', label: 'Serwis mieszalników' },
  { value: 'calibration', label: 'Kalibracja' },
  { value: 'general', label: 'Ogólny' },
];

const AVAILABILITY_OPTIONS = [
  { value: 'available', label: 'Dostępny', color: 'text-green-400', bg: 'bg-green-500/20' },
  { value: 'busy', label: 'Zajęty', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  { value: 'off_duty', label: 'Po pracy', color: 'text-gray-400', bg: 'bg-gray-500/20' },
  { value: 'on_leave', label: 'Na urlopie', color: 'text-blue-400', bg: 'bg-blue-500/20' },
];

const initialFormData: TechnicianFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  specializations: [],
  availability_status: 'available',
  is_internal: true,
  rating: 0,
  notes: '',
};

// ── Component ───────────────────────────────────────────────────────────────

export function AdminTechniciansPage() {
  const { currentOrganization, hasRole } = useAuth();

  // Data
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Search & filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSpecialization, setFilterSpecialization] = useState<string>('all');
  const [filterAvailability, setFilterAvailability] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingTechnician, setEditingTechnician] = useState<Technician | null>(null);
  const [formData, setFormData] = useState<TechnicianFormData>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canManage = hasRole(['owner', 'admin', 'manager']);

  // ── Fetch ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (currentOrganization) fetchTechnicians();
  }, [currentOrganization]);

  const fetchTechnicians = async () => {
    if (!currentOrganization) return;
    setIsLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('technicians')
        .select('*')
        .eq('organization_id', currentOrganization.organization_id)
        .order('last_name');
      if (err) throw err;
      setTechnicians(data || []);
    } catch (err) {
      console.error('Error fetching technicians:', err);
      setError('Nie udało się załadować serwisantów');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────

  const filteredTechnicians = useMemo(() => {
    return technicians.filter((t) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        t.first_name.toLowerCase().includes(q) ||
        t.last_name.toLowerCase().includes(q) ||
        t.email?.toLowerCase().includes(q) ||
        t.phone?.includes(q);
      const matchesSpecialization =
        filterSpecialization === 'all' ||
        (t.specializations && t.specializations.includes(filterSpecialization));
      const matchesAvailability =
        filterAvailability === 'all' || t.availability_status === filterAvailability;
      return matchesSearch && matchesSpecialization && matchesAvailability;
    });
  }, [technicians, searchQuery, filterSpecialization, filterAvailability]);

  const getAvailabilityInfo = (status: string) =>
    AVAILABILITY_OPTIONS.find((a) => a.value === status) || AVAILABILITY_OPTIONS[0];

  const getSpecializationLabel = (value: string) =>
    SPECIALIZATIONS.find((s) => s.value === value)?.label || value;

  // ── Handlers ──────────────────────────────────────────────────────────

  const openAddModal = () => {
    setEditingTechnician(null);
    setFormData(initialFormData);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (technician: Technician) => {
    setEditingTechnician(technician);
    setFormData({
      first_name: technician.first_name,
      last_name: technician.last_name,
      email: technician.email || '',
      phone: technician.phone || '',
      specializations: technician.specializations || [],
      availability_status: technician.availability_status,
      is_internal: technician.is_internal,
      rating: technician.rating || 0,
      notes: technician.notes || '',
    });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTechnician(null);
    setFormData(initialFormData);
    setFormError(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization) return;

    if (!formData.first_name.trim()) {
      setFormError('Imię serwisanta jest wymagane');
      return;
    }

    if (!formData.last_name.trim()) {
      setFormError('Nazwisko serwisanta jest wymagane');
      return;
    }

    setIsSaving(true);
    setFormError(null);

    try {
      const payload = {
        organization_id: currentOrganization.organization_id,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        specializations: formData.specializations,
        availability_status: formData.availability_status,
        is_internal: formData.is_internal,
        rating: formData.rating > 0 ? formData.rating : null,
        notes: formData.notes.trim() || null,
      };

      if (editingTechnician) {
        const { error: err } = await supabase
          .from('technicians')
          .update(payload)
          .eq('id', editingTechnician.id);
        if (err) throw err;
        setSuccess('Serwisant został zaktualizowany');
      } else {
        const { error: err } = await supabase
          .from('technicians')
          .insert([{ ...payload, is_active: true }]);
        if (err) throw err;
        setSuccess('Serwisant został dodany');
      }

      await fetchTechnicians();
      closeModal();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving technician:', err);
      setFormError('Nie udało się zapisać serwisanta');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      const { error: err } = await supabase.from('technicians').delete().eq('id', id);
      if (err) throw err;
      await fetchTechnicians();
      setDeleteTarget(null);
      setSuccess('Serwisant został usunięty');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting technician:', err);
      setError('Nie udało się usunąć serwisanta');
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleSpecialization = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      specializations: prev.specializations.includes(value)
        ? prev.specializations.filter((s) => s !== value)
        : [...prev.specializations, value],
    }));
  };

  // ── Rating renderer ───────────────────────────────────────────────────

  const RatingStars = ({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' }) => {
    const sizeClass = size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5';
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClass} ${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  const RatingInput = ({ value, onChange }: { value: number; onChange: (v: number) => void }) => (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star === value ? 0 : star)}
          className="p-0.5 transition-colors"
        >
          <Star
            className={`w-6 h-6 ${
              star <= value
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-600 hover:text-gray-400'
            }`}
          />
        </button>
      ))}
      {value > 0 && (
        <button
          type="button"
          onClick={() => onChange(0)}
          className="ml-2 text-xs text-gray-500 hover:text-gray-300"
        >
          Wyczyść
        </button>
      )}
    </div>
  );

  // ── Loading ───────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-56 skel" />
            <div className="h-4 w-40 skel mt-2" />
          </div>
          <div className="h-10 w-40 skel" />
        </div>
        <div className="h-10 skel" />
        <div className="card tablecard">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4 border-b border-aurora-border">
              <div className="h-10 w-10 skel" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-40 skel" />
                <div className="h-3 w-28 skel" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="pagehead">
        <div>
          <h1 className="pagehead__title flex items-center gap-2">
            <Wrench className="w-6 h-6 text-blue-400" />
            Serwisanci
          </h1>
          <p className="pagehead__sub">
            Zarządzaj serwisantami organizacji (<span className="mono">{technicians.length}</span>)
          </p>
        </div>
        {canManage && (
          <div className="pagehead__actions">
            <button onClick={openAddModal} className="btn btn--primary">
              <Plus className="w-5 h-5" />
              Dodaj serwisanta
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="statcard">
          <div className="statcard__top">
            <span className="statcard__ico">
              <Users />
            </span>
            <span className="statcard__lbl">Łącznie</span>
          </div>
          <div className="statcard__num">{technicians.length}</div>
        </div>
        <div className="statcard">
          <div className="statcard__top">
            <span className="statcard__ico bg-green-500/15 text-green-400">
              <CheckCircle />
            </span>
            <span className="statcard__lbl">Dostępnych</span>
          </div>
          <div className="statcard__num">
            {technicians.filter((t) => t.availability_status === 'available').length}
          </div>
        </div>
        <div className="statcard">
          <div className="statcard__top">
            <span className="statcard__ico bg-cyan-500/15 text-cyan-400">
              <UserCheck />
            </span>
            <span className="statcard__lbl">Wewnętrznych</span>
          </div>
          <div className="statcard__num">{technicians.filter((t) => t.is_internal).length}</div>
        </div>
        <div className="statcard">
          <div className="statcard__top">
            <span className="statcard__ico bg-orange-500/15 text-orange-400">
              <UserX />
            </span>
            <span className="statcard__lbl">Zewnętrznych</span>
          </div>
          <div className="statcard__num">{technicians.filter((t) => !t.is_internal).length}</div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-ids-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/30 rounded-ids-lg text-green-400">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Search & Filters */}
      <div className="space-y-3">
        <div className="flex gap-3">
          <div className="inputwrap flex-1">
            <Search />
            <input
              type="text"
              placeholder="Szukaj po imieniu, nazwisku, emailu, telefonie..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            aria-pressed={showFilters}
            className={`btn ${showFilters ? 'btn--tonal' : 'btn--secondary'}`}
          >
            <Filter className="w-4 h-4" />
            Filtry
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            />
          </button>
        </div>

        {showFilters && (
          <div className="card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="field">
              <label className="field__label">Specjalizacja</label>
              <select
                value={filterSpecialization}
                onChange={(e) => setFilterSpecialization(e.target.value)}
                className="select"
              >
                <option value="all">Wszystkie specjalizacje</option>
                {SPECIALIZATIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="field__label">Dostępność</label>
              <select
                value={filterAvailability}
                onChange={(e) => setFilterAvailability(e.target.value)}
                className="select"
              >
                <option value="all">Wszystkie statusy</option>
                {AVAILABILITY_OPTIONS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      {filteredTechnicians.length === 0 ? (
        <div className="card p-12 text-center">
          <Wrench className="w-12 h-12 text-aurora-subtle mx-auto mb-4" />
          <h3 className="text-lg font-medium text-aurora-text mb-2">
            {searchQuery || filterSpecialization !== 'all' || filterAvailability !== 'all'
              ? 'Nie znaleziono serwisantów'
              : 'Brak serwisantów'}
          </h3>
          <p className="text-aurora-muted mb-4">
            {searchQuery ? 'Spróbuj zmienić kryteria wyszukiwania' : 'Dodaj pierwszego serwisanta'}
          </p>
          {!searchQuery && canManage && (
            <button onClick={openAddModal} className="btn btn--primary">
              <Plus className="w-5 h-5" />
              Dodaj serwisanta
            </button>
          )}
        </div>
      ) : (
        <div className="card tablecard">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Imię i nazwisko</th>
                  <th>Specjalizacje</th>
                  <th>Typ</th>
                  <th>Dostępność</th>
                  <th>Ocena</th>
                  {canManage && <th className="text-right">Akcje</th>}
                </tr>
              </thead>
              <tbody>
                {filteredTechnicians.map((technician) => {
                  const availabilityInfo = getAvailabilityInfo(technician.availability_status);
                  return (
                    <tr key={technician.id}>
                      <td>
                        <div className="cellname">
                          <span className="avatar avatar--sm">
                            {technician.first_name?.[0]}
                            {technician.last_name?.[0]}
                          </span>
                          <div>
                            <p className="text-sm">
                              {technician.first_name} {technician.last_name}
                            </p>
                            {technician.email && (
                              <div className="flex items-center gap-1 text-xs font-normal text-aurora-subtle">
                                <Mail className="w-3 h-3" />
                                <span className="truncate max-w-[150px]">{technician.email}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {technician.specializations && technician.specializations.length > 0 ? (
                            technician.specializations.map((spec) => (
                              <span key={spec} className="badge badge--brand">
                                {getSpecializationLabel(spec)}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-aurora-subtle">-</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {technician.is_internal ? (
                          <span className="badge bg-cyan-500/15 text-cyan-400 border-transparent">
                            <Building2 className="w-3 h-3" />
                            Wewnętrzny
                          </span>
                        ) : (
                          <span className="badge bg-orange-500/15 text-orange-400 border-transparent">
                            <UserX className="w-3 h-3" />
                            Zewnętrzny
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`pill ${
                            technician.availability_status === 'available'
                              ? 'pill--active'
                              : technician.availability_status === 'busy'
                                ? 'pill--trial'
                                : technician.availability_status === 'on_leave'
                                  ? 'pill--pending'
                                  : 'bg-aurora-bg-subtle text-aurora-muted'
                          }`}
                        >
                          <span className="pin" />
                          {availabilityInfo.label}
                        </span>
                      </td>
                      <td>
                        {technician.rating && technician.rating > 0 ? (
                          <RatingStars rating={technician.rating} />
                        ) : (
                          <span className="text-sm text-aurora-subtle">
                            <StarOff className="w-4 h-4" />
                          </span>
                        )}
                      </td>
                      {canManage && (
                        <td>
                          <div className="flex items-center justify-end gap-1 rowact">
                            <button
                              onClick={() => openEditModal(technician)}
                              className="p-1.5 text-aurora-muted hover:text-aurora-text hover:bg-aurora-surface rounded-ids-md transition-colors"
                              title="Edytuj"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(technician.id)}
                              className="p-1.5 text-aurora-muted hover:text-red-400 hover:bg-red-500/10 rounded-ids-md transition-colors"
                              title="Usuń"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-aurora-scrim" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-aurora-surface border border-aurora-border rounded-ids-xl shadow-elev-4 max-w-md w-full">
            <div className="modal__head">
              <h3 className="modal__title">Potwierdzenie usuwania</h3>
            </div>
            <div className="modal__body">
              <p className="text-sm text-aurora-muted">
                Czy na pewno chcesz usunąć tego serwisanta? Tej operacji nie można cofnąć.
              </p>
            </div>
            <div className="modal__foot">
              <button onClick={() => setDeleteTarget(null)} className="btn btn--ghost">
                Anuluj
              </button>
              <button
                onClick={() => handleDelete(deleteTarget)}
                disabled={isDeleting}
                className="btn btn--danger"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-aurora-scrim backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-aurora-surface rounded-ids-xl shadow-elev-4 max-w-2xl w-full border border-aurora-border max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="modal__head">
              <div className="w-10 h-10 bg-blue-500/15 rounded-ids-md flex items-center justify-center flex-shrink-0">
                <Wrench className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="modal__title">
                  {editingTechnician ? 'Edytuj serwisanta' : 'Dodaj serwisanta'}
                </h2>
                <p className="text-sm text-aurora-muted truncate">
                  {editingTechnician
                    ? `${editingTechnician.first_name} ${editingTechnician.last_name}`
                    : 'Wypełnij dane serwisanta'}
                </p>
              </div>
              <button onClick={closeModal} className="iconbtn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <form id="technician-form" onSubmit={handleSave} className="space-y-5">
                {formError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-ids-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{formError}</p>
                  </div>
                )}

                {/* First name & Last name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block field__label mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <User className="w-4 h-4" />
                        Imię <span className="text-aurora-brand">*</span>
                      </div>
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                      placeholder="np. Jan"
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block field__label mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <User className="w-4 h-4" />
                        Nazwisko <span className="text-aurora-brand">*</span>
                      </div>
                    </label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                      placeholder="np. Kowalski"
                      className="input"
                    />
                  </div>
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block field__label mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <Mail className="w-4 h-4" />
                        Email
                      </div>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="jan.kowalski@firma.pl"
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block field__label mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <Phone className="w-4 h-4" />
                        Telefon
                      </div>
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+48 123 456 789"
                      className="input"
                    />
                  </div>
                </div>

                {/* Is internal toggle */}
                <div className="pt-4 border-t border-aurora-border">
                  <label className="block field__label mb-2">Typ serwisanta</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, is_internal: true })}
                      aria-pressed={formData.is_internal}
                      className={`flex items-center gap-2 p-3 rounded-ids-md border transition-all text-left ${
                        formData.is_internal
                          ? 'border-aurora-brand bg-aurora-brand/10 shadow-glow'
                          : 'border-aurora-border hover:border-aurora-border-strong hover:bg-aurora-bg-subtle'
                      }`}
                    >
                      <div className="p-1.5 rounded bg-cyan-500/15">
                        <Building2 className="w-4 h-4 text-cyan-400" />
                      </div>
                      <span className="text-sm text-aurora-text font-medium">Wewnętrzny</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, is_internal: false })}
                      aria-pressed={!formData.is_internal}
                      className={`flex items-center gap-2 p-3 rounded-ids-md border transition-all text-left ${
                        !formData.is_internal
                          ? 'border-aurora-brand bg-aurora-brand/10 shadow-glow'
                          : 'border-aurora-border hover:border-aurora-border-strong hover:bg-aurora-bg-subtle'
                      }`}
                    >
                      <div className="p-1.5 rounded bg-orange-500/15">
                        <UserX className="w-4 h-4 text-orange-400" />
                      </div>
                      <span className="text-sm text-aurora-text font-medium">Zewnętrzny</span>
                    </button>
                  </div>
                </div>

                {/* Specializations */}
                <div className="pt-4 border-t border-aurora-border">
                  <label className="block field__label mb-2">
                    <div className="flex items-center gap-1.5">
                      <Wrench className="w-4 h-4 text-blue-400" />
                      Specjalizacje
                    </div>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {SPECIALIZATIONS.map((spec) => {
                      const isSelected = formData.specializations.includes(spec.value);
                      return (
                        <button
                          key={spec.value}
                          type="button"
                          onClick={() => toggleSpecialization(spec.value)}
                          aria-pressed={isSelected}
                          className="chip"
                        >
                          {spec.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Availability status */}
                <div className="pt-4 border-t border-aurora-border">
                  <label className="block field__label mb-1.5">Status dostępności</label>
                  <select
                    value={formData.availability_status}
                    onChange={(e) =>
                      setFormData({ ...formData, availability_status: e.target.value })
                    }
                    className="select"
                  >
                    {AVAILABILITY_OPTIONS.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Rating */}
                <div className="pt-4 border-t border-aurora-border">
                  <label className="block field__label mb-2">
                    <div className="flex items-center gap-1.5">
                      <Star className="w-4 h-4" />
                      Ocena serwisanta
                    </div>
                  </label>
                  <RatingInput
                    value={formData.rating}
                    onChange={(v) => setFormData({ ...formData, rating: v })}
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block field__label mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <FileText className="w-4 h-4" />
                      Notatki
                    </div>
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    placeholder="Dodatkowe informacje o serwisancie..."
                    className="textarea resize-none"
                  />
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-aurora-border">
              <p className="text-xs text-aurora-subtle">
                <span className="text-aurora-brand">*</span> Pola wymagane
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={closeModal} className="btn btn--secondary">
                  Anuluj
                </button>
                <button
                  type="submit"
                  form="technician-form"
                  disabled={isSaving}
                  className="btn btn--primary min-w-[140px]"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {editingTechnician ? 'Zapisz zmiany' : 'Dodaj serwisanta'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTechniciansPage;
